import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localePt from '@angular/common/locales/pt';
import localeRu from '@angular/common/locales/ru';
import localeUk from '@angular/common/locales/uk';
import type { ApplicationConfig } from '@angular/core';
import { afterNextRender, ErrorHandler, inject, isDevMode, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withPreloading } from '@angular/router';

import { authInterceptor } from '@rpgc-ai/services/auth/auth.interceptor';
import { CDN_REPLACE_CONFIG,CDN_REPLACE_TOKEN } from '@rpgc-ai/services/cdn-replace/cdn-replace.data';
import { WebKeyboardInsetService } from '@rpgc-ai/services/keyboard/web-keyboard-inset.service';
import { MODAL_REGISTRY } from '@rpgc-ai/services/modal/modal-registry.token';
import { IdlePreloadStrategy } from '@rpgc-ai/services/routing/idle-preload.strategy';
import {
  CAICHAT_SOCIALS_CONFIG,
  CAICHAT_SUPPORT_EMAIL,
  SOCIAL_LINKS_TOKEN,
  SUPPORT_EMAIL_TOKEN,
} from '@rpgc-ai/services/social-link/social-link.const';
import { SPEECH_RECOGNITION_BACKEND } from '@rpgc-ai/services/speech-to-text/speech-backend';
import { WebSpeechBackend } from '@rpgc-ai/services/speech-to-text/web-speech.backend';
import { storageServicePersistStorageFactory } from '@rpgc-ai/services/storage/storage.service';
import { SUPPORTED_LANGUAGES_CODE } from '@rpgc-ai/services/translate/languages';
import { TranslocoHttpLoader } from '@rpgc-ai/services/translate/transloco-loader';

import { MODAL_SHELL_COMPONENT } from '@rpgc-ai/shared-interfaces/tokens/modal.token';

import { environment, EXTENSION_HOST_ENABLED, IS_RPGC, IS_TMA } from '@rpgc-ai/environments';
import { ModalComponent } from '@rpgc-ai/lazy-blocks/modal/modal.component';

import { provideTransloco } from '@jsverse/transloco';
import type { GetLangParams } from '@jsverse/transloco-persist-lang';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHighcharts } from 'highcharts-angular';

import { appRoutes } from './app.routes';
import { BufferedErrorHandler } from './buffered-error-handler';
import { modalRegistry } from './modal.registry';

// Регистрация РУ локали, чтобы в angular-пайпах можно было использовать ru-RU
registerLocaleData(localeDe);
registerLocaleData(localePt);
registerLocaleData(localeRu);
registerLocaleData(localeUk);

// Определяем базовых провайдеров, которые будут во всех режимах
const baseProviders = [
  provideZonelessChangeDetection(),
  provideClientHydration(withEventReplay()),
  provideAnimationsAsync(),
  provideRouter(appRoutes, withPreloading(IdlePreloadStrategy)),
  // Вне dev devtools не подключаются: иначе они инструментируют каждый экшен, держат историю
  // состояний в памяти и открывают состояние приложения расширению браузера.
  ...(isDevMode()
    ? [
        provideStoreDevtools({
          maxAge: 25,
          autoPause: true,
          trace: false,
          traceLimit: 75,
          // Zoneless-режим: подключение вне зоны Angular.
          connectInZone: false,
        }),
      ]
    : []),
  provideHttpClient(withInterceptors([authInterceptor]), withFetch()),
  provideTransloco({
    config: {
      availableLangs: SUPPORTED_LANGUAGES_CODE,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
      missingHandler: {
        useFallbackTranslation: false,
      },
    },
    loader: TranslocoHttpLoader,
  }),
  provideTranslocoPersistLang({
    storage: { useFactory: storageServicePersistStorageFactory },
    storageKey: 'selectedLanguage',
    getLangFn: ({ cachedLang, browserLang, defaultLang }: GetLangParams) => {
      return cachedLang || browserLang || defaultLang;
    },
  }),
  provideHighcharts(),
  { provide: IS_RPGC, useValue: false },
  { provide: IS_TMA, useValue: false },
  { provide: EXTENSION_HOST_ENABLED, useValue: environment.EXTENSION_HOST_ENABLED ?? true },
];

// Обработчик держит ошибки до того, как main.ts догрузит Sentry и подключится к нему.
const sentryProviders = isDevMode() ? [] : [{ provide: ErrorHandler, useClass: BufferedErrorHandler }];

const customProviders = [
  { provide: SOCIAL_LINKS_TOKEN, useValue: CAICHAT_SOCIALS_CONFIG },
  { provide: SUPPORT_EMAIL_TOKEN, useValue: CAICHAT_SUPPORT_EMAIL },
  { provide: MODAL_REGISTRY, useValue: modalRegistry },
  { provide: MODAL_SHELL_COMPONENT, useValue: ModalComponent },
  { provide: SPEECH_RECOGNITION_BACKEND, useClass: WebSpeechBackend },
  { provide: CDN_REPLACE_TOKEN, useValue: CDN_REPLACE_CONFIG },
];

const keyboardProviders = [
  provideAppInitializer(() => {
    const keyboard = inject(WebKeyboardInsetService);
    afterNextRender(() => keyboard.start());
  }),
];

export const appConfig: ApplicationConfig = {
  providers: [...baseProviders, ...sentryProviders, ...customProviders, ...keyboardProviders],
};
